<template>
  <div>
    <HeroSection
      :nav-breadcrumbs="[
        {
          href: '/training',
          title: 'Training'
        },
        {
          href: '/training/workshops',
          title: 'Workshops'
        },
        {
          href: canonicalPaths.relative,
          title: workshop.abbreviated_name || workshop.name
        }
      ]"
      :value="heroSection"
    >
      <template v-if="workshopPage.hero_section.cta" #cta>
        <HeroCta :value="workshopPage.hero_section.cta" />
      </template>
    </HeroSection>

    <template v-if="workshop?.sell_bullets">
      <SellOccasionsSection
        :scroll-to-section="scrollToSection"
        :value="workshop"
        class="mb-16"
      />
      <CourseAboutSection
        :course="workshop"
        :value="introSection"
        class="mb-16"
      />
    </template>
    <CourseIntroSection
      v-else
      :course="workshop"
      :scroll-to-section="scrollToSection"
      :value="introSection"
      class="mb-16 hero-section hero-below-offset"
    />

    <MidPageNavBar
      :nav-items="[
        {
          key: 'overview',
          title: 'Overview'
        },
        {
          key: 'trainers',
          title: 'Trainers'
        },
        {
          key: 'reviews',
          hidden: !reviews.length,
          title: 'Reviews'
        },
        {
          key: 'register',
          title: 'Find a Class'
        },
        {
          key: 'resources',
          title: 'Resources'
        }
      ]"
      class="mb-1"
      @click="scrollToSection"
    />

    <CourseOverviewSection
      :ref="
        el => {
          sectionRefs.overview = el
        }
      "
      :course="workshop"
      :value="workshopPage.course_overview_section"
      class="mb-16"
    />

    <CourseDetailSection
      v-if="workshop.detail_section"
      :course="workshop"
      :value="workshopPage.course_detail_section"
      class="mb-16"
    />

    <DividerSection v-if="workshop.detail_section" class="mb-16" />

    <TrainerPersonsSection
      :ref="
        el => {
          sectionRefs.trainers = el
        }
      "
      :filters-value="{ productCodes }"
      :value="workshopPage.trainers_section"
      class="mb-16"
      hide-filters
      upcoming
    />

    <ReviewsSection
      :ref="
        el => {
          sectionRefs.reviews = el
        }
      "
      :items="reviews"
      class="mb-16"
    />

    <v-container
      :ref="
        el => {
          sectionRefs.register = el
        }
      "
    />

    <v-lazy>
      <OccasionsSection
        :banner="banner"
        :filters="{ what: false, who: false }"
        :filters-value="{ productCodes }"
        :value="workshopPage.classes_section"
        class="mb-16"
        hide-info-button
      />
    </v-lazy>

    <IconDifferentiatorsSection
      class="mb-16"
      :value="iconDifferentiatorsSection"
    />

    <PromotedResourcesSection
      :ref="
        el => {
          sectionRefs.resources = el
        }
      "
      :value="workshopPage.promoted_resources_section"
      class="mb-16"
    />

    <DividerSection class="mb-16" />

    <ContactCtaSection
      :value="workshopPage.contact_cta_section"
      class="mb-16"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive } from 'vue'
import { useDisplay } from 'vuetify'
import { NAV_BAR_HEIGHT } from '#root/lib/consts'
import { scrollToRefKey } from '#root/lib/utils'
import { usePageContext } from '#root/renderer/usePageContext'
import { useProvideDeliveryMethodsStore } from '#root/lib/useDeliveryMethodsStore'
import { useProvideTrainerPersonsStore } from '#root/lib/useTrainerPersonsStore'

const { lgAndUp } = useDisplay()
const sectionRefs = reactive({})

const props = defineProps({
  banner: {
    default: undefined,
    type: Object
  },
  deliveryMethods: {
    required: true,
    type: Array
  },
  reviews: {
    required: true,
    type: Array
  },
  trainers: {
    required: true,
    type: Array
  },
  productCodes: {
    required: true,
    type: Array
  },
  workshop: {
    required: true,
    type: Object
  },
  workshopPage: {
    required: true,
    type: Object
  }
})

useProvideDeliveryMethodsStore(props.deliveryMethods)
useProvideTrainerPersonsStore(props.trainers)

const { canonicalPaths } = usePageContext()
const heroSection = computed(() => {
  const section = Object.assign({}, props.workshopPage.hero_section)
  section.title = props.workshop.abbreviated_name
    ? `${props.workshop.name} (${props.workshop.abbreviated_name})`
    : props.workshop.name
  return section
})
const iconDifferentiatorsSection = computed(() => {
  return Object.assign(
    {},
    props.workshopPage.icon_differentiators_section,
    props.workshop.icon_differentiators_section
  )
})
const introSection = computed(() => {
  return Object.assign(
    {},
    props.workshopPage.course_intro_section,
    props.workshop.intro_section
  )
})

function scrollToSection({ key }) {
  scrollToRefKey(sectionRefs, key, {
    height: lgAndUp.value ? NAV_BAR_HEIGHT : 0
  })
}

onMounted(async () => {
  await scrollToRefKey(sectionRefs, undefined, {
    delay: true,
    height: lgAndUp.value ? NAV_BAR_HEIGHT : 0
  })
})
</script>
